import { PlusIcon } from '@heroicons/react/solid';
import { Node, useReactFlow } from '@xyflow/react';
import { useAtom, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Option, Text } from '@/lib/v2/components';

import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue } from '@/src/infrastructure/Protocol/useEmblue';

import Conditional from './Conditional';
import HeaderSidePanel from './HeaderSidePanel';

import {
  atomConditionsValues,
  atomSidePanelIsOpen,
  ConditionState,
} from '@/modules/AutomationModuleNew/atoms/flows';
import { TYPE_GROUPS } from '@/modules/AutomationModuleNew/constants/flows';
import {
  selectBelongOptions,
  selectTypesOptions,
} from '@/modules/AutomationModuleNew/hooks/Journeys/useBinaryConditional';
import {
  BelongsToGroupConnectionConditionType,
  BelongsToGroupOrTagConnectionConditionType,
  BelongsToTagConnectionConditionType,
  BinaryConditionalData,
  LogicalConnectionConditionOperator,
} from '@/modules/AutomationModuleNew/types/flowsTypes';

type ConditionalFormProps = Node<BinaryConditionalData>;

const ConditionalForm = (props: ConditionalFormProps) => {
  const { data, id: nodeId } = props;
  const { t } = useTranslation();
  const { updateNodeData } = useReactFlow();
  const [conditionsValues, setConditionValues] = useAtom(atomConditionsValues);

  const [tagsList] = useEmblue(ServiceMethods.getTags, {
    page: 1,
    pageSize: 1000,
  });
  const [groupsList] = useEmblue(ServiceMethods.getGroups, {
    identifier: 'GROUPS_VIEW',
    page: 1,
    pageSize: 1000,
  });

  const tagsOptions = useMemo(() => {
    if (!tagsList) return [];
    return tagsList.list.map((tag) => ({
      id: tag.tag.id,
      name: tag.tag.name,
      value: tag.tag.id,
    }));
  }, [tagsList]);

  const groupsOptions = useMemo(() => {
    if (!groupsList) return [];
    return groupsList.list.map((group) => ({
      id: group.group.id,
      name: group.group.groupName,
      value: group.group.id,
    }));
  }, [groupsList]);

  const setSidePanelIsOpen = useSetAtom(atomSidePanelIsOpen);

  const handleCloseSidePanel = useCallback(() => {
    setSidePanelIsOpen(false);
  }, [setSidePanelIsOpen]);

  const addNewCondition = useCallback(() => {
    const newCondition: ConditionState = {
      selectTypeValue: selectTypesOptions[0],
      selectBelongValue: selectBelongOptions[0],
      selectIdsValues: [],
    };
    setConditionValues((prev) => {
      return [...prev, newCondition];
    });
  }, [setConditionValues]);

  const handleSave = useCallback(() => {
    updateNodeData(nodeId, {
      input: {
        ...data.input,
        condition: {
          ...data.input.condition,
          conditions: conditionsValues.map((condition) => {
            const selectTypeValue = condition.selectTypeValue;
            const selectBelongValue = condition.selectBelongValue;

            const selectIdsValues = condition.selectIdsValues.map((item) => Number(item.value));

            return {
              type: selectTypeValue.value,
              operator: selectBelongValue.value,
              ...(selectTypeValue.value === TYPE_GROUPS
                ? { groupIds: selectIdsValues }
                : { tagIds: selectIdsValues }),
            };
          }),
        },
      },
    } as Partial<ConditionalFormProps>);
    handleCloseSidePanel();
  }, [conditionsValues, data.input, handleCloseSidePanel, nodeId, updateNodeData]);

  useEffect(() => {
    if (data.input.condition?.conditions.length === 0) {
      const newCondition: ConditionState = {
        selectTypeValue: selectTypesOptions[0],
        selectBelongValue: selectBelongOptions[0],
        selectIdsValues: [],
      };
      setConditionValues([newCondition]);
    } else {
      const conditionsState: ConditionState[] = data.input.condition?.conditions.map((con) => {
        const condition = con as BelongsToGroupOrTagConnectionConditionType;

        const selectTypeValue = selectTypesOptions.find(
          (item) => item.value === condition.type
        ) as Option;

        const selectBelongValue = selectBelongOptions.find(
          (item) => item.value === condition.operator
        ) as Option;

        const selectIdsValues =
          selectTypeValue.value === TYPE_GROUPS
            ? groupsOptions.filter((item) =>
                (
                  condition as BelongsToGroupConnectionConditionType & {
                    operator?: LogicalConnectionConditionOperator;
                  }
                ).groupIds.includes(Number(item.value))
              )
            : tagsOptions.filter((item) =>
                (
                  condition as BelongsToTagConnectionConditionType & {
                    operator?: LogicalConnectionConditionOperator;
                  }
                ).tagIds.includes(Number(item.value))
              );

        return {
          selectTypeValue,
          selectBelongValue,
          selectIdsValues,
        };
      }) as ConditionState[];

      setConditionValues(conditionsState);
    }
  }, [data.input.condition?.conditions, groupsOptions, setConditionValues, tagsOptions]);

  return (
    <div className="flex w-full flex-col">
      <HeaderSidePanel title={t('condition')} onCancel={handleCloseSidePanel} onSave={handleSave} />
      <div className="flex w-full flex-col p-6">
        <Text variant="text">{t('ConditionalForm.contact.description')}</Text>
        {conditionsValues.map((condition, index) => {
          return (
            <>
              <Conditional
                key={index}
                conditionState={condition}
                groupsOptions={groupsOptions}
                index={index}
                tagsOptions={tagsOptions}
              />
              {index !== conditionsValues.length - 1 && (
                <Text className="py-4 pl-3" variant="text-sm">
                  OR
                </Text>
              )}
            </>
          );
        })}
        <Button
          onlyIcon
          secondary
          standard
          iconLeft={<PlusIcon className="size-4" />}
          size="small"
          onClick={addNewCondition}
        >
          OR
        </Button>
      </div>
    </div>
  );
};

export default memo(ConditionalForm);
